import * as React from 'react';
import { isNullOrEmpty } from 'common-utils';
import { DeviceExecutionDto } from 'dto/welcome-pack.dto';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { observer } from 'mobx-react';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { TableUtils } from 'pages/private/components/table-utils';
import { useStores } from 'store';
import { IconTextButton, showConfirmDialog, Skeleton, Spinner, Typography } from 'ui-lib';
import welcomePackCss from './welcome-pack.css';
import { MessagesAlert } from 'pages/private/components/alert/messages-alert';
import { useWelcomePackQueryManager } from './utils';
import {
    DEFAULT_DEVICE_COLUMN,
    DevicesTableComponent,
    ISelectedRows,
} from 'pages/private/components/devices-table-component';

interface IStepTableProps {
    key: string;
    type: string;
    name?: string;
    dataSource: DeviceExecutionDto[];
}

const HIDDEN_COLUMNS = [
    DEFAULT_DEVICE_COLUMN.DEVICE_GROUP,
    DEFAULT_DEVICE_COLUMN.STATUS,
    DEFAULT_DEVICE_COLUMN.LAST_UPDATE,
];
export const WelcomePackTracking = observer(() => {
    const { errorMessages, loading, withLoading, saving, withSaving } = useLoadingStates();
    const { welcomePackStore, deviceGroupsStore } = useStores();

    const { welcomePack, accountSelector, selectedAccount, failureStepConfiguration, ...welcomePackStates } =
        useWelcomePackQueryManager();

    const [deviceExecutionsMapped, setDeviceExecutions] = React.useState<Record<string, IStepTableProps>>({});

    async function loadData() {
        if (!welcomePack || !selectedAccount) {
            return;
        }
        return withLoading(async () => {
            const deviceExecutions = await Promise.all(
                welcomePack.allStepConfigurations.map(async (step) => {
                    const devices = await welcomePackStore.getDeviceExecutionsByStep(
                        selectedAccount.id,
                        selectedAccount.uuid,
                        welcomePack.id,
                        step.key,
                    );
                    return {
                        key: step.key,
                        name: step.name,
                        type: step.type,
                        dataSource: devices,
                    };
                }),
            );

            setDeviceExecutions(
                deviceExecutions.reduce((acc, step) => {
                    acc[step.key] = step;
                    return acc;
                }, {}),
            );
        });
    }

    const [decommissioningDevices, setDecommissioningDevices] = React.useState<ISelectedRows>(null);

    React.useEffect(() => {
        loadData();
    }, [welcomePack]);

    return (
        <Spinner spinning={saving} className={welcomePackCss['loading-spinner']}>
            <div className={welcomePackCss['tracking']}>
                <OrganisationSelect
                    placeholder='Organisation'
                    treeDataSource={accountSelector.dataSource}
                    filterTreeNode={(input, treeNode) => {
                        return treeNode.label?.includes(input);
                    }}
                    expandAll={true}
                    onChange={accountSelector.onChange}
                    value={accountSelector.selectedAccount?.id}
                />
                <IconTextButton
                    className={welcomePackCss['top-right-action']}
                    label='Refresh'
                    htmlType='button'
                    onClick={loadData}
                />
                <Skeleton loading={welcomePackStates.loading}>
                    <div style={{ marginTop: '10px' }}>
                        <MessagesAlert errorMessages={errorMessages} warningMessages={welcomePackStates.warningMessage} />
                        {welcomePackStates.isActive && (
                            <React.Fragment key={'welcome-pack'}>
                                <Typography.Title level={3}>{welcomePack?.label}</Typography.Title>
                                {welcomePackStates.processingSteps.map((step) => {
                                    return (
                                        <DevicesTableComponent
                                            key={step.key}
                                            title={step?.name}
                                            className={welcomePackCss['step-configuration-table']}
                                            dataSource={deviceExecutionsMapped[step.key]?.dataSource}
                                            loadingState={loading}
                                            buildDeviceLinkProps={(_) => ({
                                                accountId: selectedAccount.id,
                                            })}
                                            extraColumns={[
                                                TableUtils.createBasicFilterColumn({
                                                    key: 'jobExecutionStatus',
                                                    title: 'Execution Status',
                                                }),
                                            ]}
                                            hiddenDefaultColumns={HIDDEN_COLUMNS}
                                        />
                                    );
                                })}
                                <DevicesTableComponent
                                    title={failureStepConfiguration?.name ?? 'Failed Devices'}
                                    className={welcomePackCss['step-configuration-table']}
                                    dataSource={deviceExecutionsMapped[failureStepConfiguration?.key]?.dataSource}
                                    loadingState={loading}
                                    buildDeviceLinkProps={(_) => ({
                                        accountId: selectedAccount.id,
                                    })}
                                    hiddenDefaultColumns={HIDDEN_COLUMNS}
                                    rowSelection={{
                                        selectedRows: decommissioningDevices,
                                        onChange: (selectedRows: ISelectedRows): void => {
                                            setDecommissioningDevices(selectedRows);
                                        },
                                    }}
                                    extraColumns={[
                                        TableUtils.createBasicFilterColumn({
                                            key: 'jobExecutionFailureStep',
                                            title: 'Failure Step',
                                            columnExtraOptions: {
                                                render(value, record) {
                                                    return welcomePackStates.processingSteps.find(
                                                        (step) => step.key === record.jobExecutionFailureStep,
                                                    )?.name;
                                                },
                                            },
                                        }),
                                        TableUtils.createBasicFilterColumn({
                                            key: 'jobExecutionFailureReason',
                                            title: 'Failure Reason',
                                        }),
                                    ]}
                                    extraItems={[
                                        <IconTextButton
                                            key='revoke'
                                            label='Revoke Devices'
                                            disabled={isNullOrEmpty(decommissioningDevices?.selectedKeys)}
                                            onClick={async (): Promise<void> => {
                                                showConfirmDialog({
                                                    title: 'Revoke Devices',
                                                    content: 'Are you sure you want to revoke the selected devices?',
                                                    okText: 'OK',
                                                    cancelText: 'Cancel',
                                                    onOk: async () => {
                                                        await withSaving(async () => {
                                                            await Promise.all(
                                                                decommissioningDevices.selectedKeys.map((deviceName) =>
                                                                    deviceGroupsStore.revokeDevicesFromGroup(
                                                                        selectedAccount.id,
                                                                        [deviceName],
                                                                        failureStepConfiguration.groupName,
                                                                    ),
                                                                ),
                                                            );
                                                            return loadData();
                                                        });
                                                        setDecommissioningDevices(null);
                                                    },
                                                });
                                            }}
                                        />,
                                    ]}
                                />
                            </React.Fragment>
                        )}
                    </div>
                </Skeleton>
                <IconTextButton
                    className={welcomePackCss['footer-left-button']}
                    htmlType='button'
                    type='default'
                    label='Back to welcome packs'
                    onClick={welcomePackStates.onBackToWelcomePacks}
                />
            </div>
        </Spinner>
    );
});
